import React, { useMemo, useState, useEffect, isValidElement } from 'react';
import { Star, DollarSign } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  calculateDeadlineStatus,
  calculateWorkingDays,
  calculateProjectedCompletionDate,
  buildDeadlineTooltip
} from '../lib/dateHelpers';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const getProgressColor = (percentage) => {
  if (percentage >= 100) return '#ef4444';
  if (percentage >= 90) return '#f97316';
  if (percentage >= 75) return '#f59e0b';
  return '#10b981';
};

const formatHours = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '0h';
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded}${rounded === 1 ? 'h' : 'h'}`;
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return `$0.00`;
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    return `$${amount.toFixed(2)}`;
  }
};

const getBudgetStatusBadge = (percentage) => {
  if (percentage >= 100) return { label: '🚨 Over Budget', className: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 border border-red-200 dark:border-red-800' };
  if (percentage >= 90) return { label: '⚠️ At Risk', className: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-800' };
  if (percentage >= 75) return { label: '⚠️ At Risk', className: 'bg-yellow-50 dark:bg-yellow-900/30 text-amber-600 dark:text-amber-300 border border-yellow-200 dark:border-yellow-800' };
  return { label: '✓ On Track', className: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' };
};

const ProgressBar = ({
  label,
  percent,
  spentLabel,
  remainingLabel,
  ariaLabel
}) => (
    <div className="space-y-1.5">
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors">
      <span className="text-gray-700 dark:text-gray-300 transition-colors">{label}</span>
      <span className="font-mono text-gray-700 dark:text-gray-300 transition-colors">{spentLabel}</span>
    </div>
    <div
      className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden transition-colors"
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuenow={Math.min(percent, 150)}
      aria-valuemin={0}
      aria-valuemax={150}
    >
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{
          width: `${Math.min(percent, 100)}%`,
          backgroundColor: getProgressColor(percent)
        }}
      />
    </div>
    {remainingLabel && (
      <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors">{remainingLabel}</div>
    )}
  </div>
);

const statusColorMap = {
  green: '#047857',
  yellow: '#92400e',
  orange: '#9a3412',
  red: '#991b1b',
  blue: '#1e3a8a',
  gray: '#1f2937'
};

const mapStatusString = (status) => {
  switch (status) {
    case 'completed':
      return { label: 'Completed', color: 'green', bgColor: '#d1fae5', textColor: statusColorMap.green };
    case 'in_progress':
      return { label: 'In Progress', color: 'yellow', bgColor: '#fef3c7', textColor: statusColorMap.yellow };
    case 'overdue':
      return { label: 'Overdue', color: 'red', bgColor: '#fee2e2', textColor: statusColorMap.red };
    case 'blocked':
      return { label: 'Blocked', color: 'orange', bgColor: '#fed7aa', textColor: statusColorMap.orange };
    case 'not_started':
      return { label: 'Not Started', color: 'gray', bgColor: '#f3f4f6', textColor: statusColorMap.gray };
    default:
      if (typeof status === 'string') {
        return { label: status.replace(/_/g, ' '), color: 'gray', bgColor: '#f3f4f6', textColor: statusColorMap.gray };
      }
      return null;
  }
};

const ProjectCard = ({
  project,
  onClick,
  className,
  footer,
  actions,
  showHover = true,
  disableNavigation = false,
  onDeadlineAction,
  onBudgetAction
}) => {
  const {
    name,
    color,
    budget = {},
    spent = {},
    hourly_rate: hourlyRate = {},
    hours_this_week: hoursThisWeek = 0,
    earnings_this_week: earningsThisWeek = 0,
    alerts = [],
    status: rawStatus,
    category,
    tags = [],
    is_billable: isBillable,
    is_default: isDefault,
    timeline
  } = project || {};

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (!timeline?.due_date) return undefined;
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, [timeline?.due_date]);

  const handleDeadlineClick = (event) => {
    event.stopPropagation();
    if (!disableNavigation) {
      onDeadlineAction?.(project);
    }
  };

  const handleBudgetClick = (event) => {
    event.stopPropagation();
    if (!disableNavigation) {
      onBudgetAction?.(project);
    }
  };

  const normalizedStatus = useMemo(() => {
    if (!rawStatus) return null;
    if (typeof rawStatus === 'string') {
      return mapStatusString(rawStatus);
    }
    if (typeof rawStatus === 'object') {
      const mapped = mapStatusString(rawStatus.value || rawStatus.id || rawStatus.status);
      return {
        label: rawStatus.label || mapped?.label || '',
        color: rawStatus.color || mapped?.color || 'green',
        bgColor: rawStatus.bgColor || mapped?.bgColor || '#d1fae5',
        textColor: rawStatus.textColor || (rawStatus.color ? (statusColorMap[rawStatus.color] || '#047857') : mapped?.textColor || '#047857')
      };
    }
    return null;
  }, [rawStatus]);

  const computed = useMemo(() => {
    const timeBudget = budget?.time_hours || 0;
    const moneyBudget = budget?.money_amount || 0;
    const currency = budget?.currency || hourlyRate?.currency || 'USD';

    const spentHours = spent?.time_hours ?? 0;
    const rateAmount = hourlyRate?.amount ?? 0;
    const calculatedMoneySpent = spent?.money_amount !== undefined
      ? spent.money_amount
      : rateAmount ? spentHours * rateAmount : 0;

    const timePercent = timeBudget > 0 ? (spentHours / timeBudget) * 100 : 0;
    const moneyPercent = moneyBudget > 0 ? (calculatedMoneySpent / moneyBudget) * 100 : 0;

    const timeRemaining = timeBudget > 0 ? timeBudget - spentHours : null;
    const moneyRemaining = moneyBudget > 0 ? moneyBudget - calculatedMoneySpent : null;

    return {
      timeBudget,
      moneyBudget,
      currency,
      spentHours,
      calculatedMoneySpent,
      timePercent,
      moneyPercent,
      timeRemaining,
      moneyRemaining
    };
  }, [budget, hourlyRate, spent]);

  const budgetPercentUsed = useMemo(() => {
    const values = [computed.timePercent, computed.moneyPercent].filter((val) => Number.isFinite(val));
    return values.length > 0 ? Math.max(...values) : 0;
  }, [computed.moneyPercent, computed.timePercent]);

  const deadlineStatus = useMemo(
    () => calculateDeadlineStatus(timeline?.due_date, budgetPercentUsed, currentTime),
    [timeline?.due_date, budgetPercentUsed, currentTime]
  );

  const workingDaysRemaining = useMemo(() => {
    if (!timeline?.due_date || !deadlineStatus?.dueDate) return null;
    return calculateWorkingDays(currentTime, deadlineStatus.dueDate);
  }, [timeline?.due_date, deadlineStatus?.dueDate, currentTime]);

  const projectedCompletion = useMemo(() => {
    if (!timeline?.start_date) return null;
    return calculateProjectedCompletionDate(
      timeline.start_date,
      timeline.estimated_hours,
      computed.spentHours,
      currentTime
    );
  }, [timeline?.start_date, timeline?.estimated_hours, computed.spentHours, currentTime]);

  const deadlineTooltip = useMemo(() => {
    if (!deadlineStatus) return '';
    return buildDeadlineTooltip({
      dueDate: deadlineStatus.dueDate,
      status: deadlineStatus,
      workingDays: workingDaysRemaining,
      projectedCompletion
    });
  }, [deadlineStatus, workingDaysRemaining, projectedCompletion]);

  const showDeadlinePulse =
    deadlineStatus &&
    (deadlineStatus.urgency === 'critical' || deadlineStatus.urgency === 'urgent') &&
    deadlineStatus.daysLeft !== null;

  const budgetStatus = useMemo(() => {
    if (computed.timeBudget) {
      return getBudgetStatusBadge(computed.timePercent);
    }
    if (computed.moneyBudget) {
      return getBudgetStatusBadge(computed.moneyPercent);
    }
    return null;
  }, [computed.timeBudget, computed.timePercent, computed.moneyBudget, computed.moneyPercent]);

  const handleKeyDown = (event) => {
    if (!disableNavigation && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick?.(project);
    }
  };

  return (
    <article
      className={cn(
        'relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-transparent dark:border-gray-700 rounded-2xl shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400',
        showHover && 'hover:shadow-2xl hover:-translate-y-1',
        disableNavigation ? 'cursor-default' : 'cursor-pointer',
        className
      )}
      onClick={() => {
        if (!disableNavigation) {
          onClick?.(project);
        }
      }}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Open project ${name}`}
    >
      <div className="p-5 sm:p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div
              className="w-3 h-12 rounded-full flex-shrink-0"
              style={{ backgroundColor: color || '#06b6d4' }}
              aria-hidden="true"
            />
              <div className="space-y-2 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
                  {name}
                </h3>
                {isDefault && (
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-yellow-600 dark:text-yellow-400 transition-colors">
                    <Star className="w-4 h-4 fill-yellow-400 dark:fill-yellow-500 text-yellow-500 dark:text-yellow-400" />
                    Default
                  </span>
                )}
                {isBillable && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-colors">
                    Billable
                  </span>
                )}
                {hourlyRate?.amount && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 transition-colors">
                    <DollarSign className="w-3 h-3 mr-1" />
                    {hourlyRate.currency || 'USD'} {hourlyRate.amount}/h
                  </span>
                )}
                {budgetStatus && (
                  <span className={cn('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold', budgetStatus.className)}>
                    {budgetStatus.label}
                  </span>
                )}
                {normalizedStatus?.label && (
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: normalizedStatus.bgColor,
                      color: normalizedStatus.textColor || statusColorMap[normalizedStatus.color] || '#047857'
                    }}
                  >
                    {normalizedStatus.label}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400 transition-colors">
                {category && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 font-medium transition-colors">
                    {category}
                  </span>
                )}
                {tags?.map((tag) => (
                  <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 font-medium transition-colors">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {actions && (
            <div
              className="flex items-center gap-2"
              onClick={(event) => event.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>

        {deadlineStatus?.daysLeft !== null ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleDeadlineClick}
                  className={cn(
                    'w-full text-left rounded-xl border px-3 py-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2',
                    showDeadlinePulse && 'animate-pulse',
                    'border-transparent'
                  )}
                  style={{
                    backgroundColor: deadlineStatus.background,
                    color: deadlineStatus.color
                  }}
                  aria-label={deadlineStatus.ariaLabel}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span className="text-lg leading-none">{deadlineStatus.icon}</span>
                    <span className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <span>{deadlineStatus.text}</span>
                      {deadlineStatus.formattedDate && (
                        <span className="text-sm font-normal opacity-80">
                          ({deadlineStatus.formattedDate})
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm"
                      style={{ backgroundColor: deadlineStatus.color }}
                    >
                      {deadlineStatus.statusLabel}
                    </span>
                    {deadlineStatus.warning && (
                      <span className="text-xs font-medium text-red-600 bg-white/70 px-2 py-1 rounded-full">
                        ⚠️ {deadlineStatus.warning}
                      </span>
                    )}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs whitespace-pre-wrap bg-gray-900 text-white">
                {deadlineTooltip || 'No additional details'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div
            className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-2 transition-colors"
            aria-label="Deadline: No deadline set"
          >
            <span>No deadline</span>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <button
              type="button"
              onClick={handleDeadlineClick}
              className="underline text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 rounded transition-colors"
            >
              Set deadline
            </button>
          </div>
        )}

        {(computed.timeBudget || computed.moneyBudget) ? (
          <div className="space-y-4">
            {computed.timeBudget ? (
              <ProgressBar
                label="Time Budget"
                percent={computed.timePercent}
                spentLabel={`${formatHours(computed.spentHours)} / ${formatHours(computed.timeBudget)} (${Math.round(computed.timePercent)}%)`}
                remainingLabel={
                  computed.timeRemaining !== null
                    ? computed.timeRemaining >= 0
                      ? `${formatHours(computed.timeRemaining)} remaining`
                      : `${formatHours(Math.abs(computed.timeRemaining))} over`
                    : null
                }
                ariaLabel={`Time budget ${Math.round(computed.timePercent)} percent used`}
              />
            ) : null}

            {computed.moneyBudget ? (
              <ProgressBar
                label="Money Budget"
                percent={computed.moneyPercent}
                spentLabel={`${formatCurrency(computed.calculatedMoneySpent, computed.currency)} / ${formatCurrency(computed.moneyBudget, computed.currency)} (${Math.round(computed.moneyPercent)}%)`}
                remainingLabel={
                  computed.moneyRemaining !== null
                    ? computed.moneyRemaining >= 0
                      ? `${formatCurrency(computed.moneyRemaining, computed.currency)} remaining`
                      : `${formatCurrency(Math.abs(computed.moneyRemaining), computed.currency)} over`
                    : null
                }
                ariaLabel={`Money budget ${Math.round(computed.moneyPercent)} percent used`}
              />
            ) : null}
          </div>
        ) : (
          <div className="p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-600 dark:text-gray-400 transition-colors">
            Budget not set.{' '}
            <button
              type="button"
              onClick={handleBudgetClick}
              className="underline text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 rounded transition-colors"
            >
              Add a budget to track progress.
            </button>
          </div>
        )}

        <div className="text-sm text-gray-600 dark:text-gray-400 font-medium flex flex-wrap items-center gap-2 transition-colors">
          {hourlyRate?.amount ? (
            <span>Rate: {formatCurrency(hourlyRate.amount, hourlyRate.currency || 'USD')}/hr</span>
          ) : (
            <span className="text-gray-500 dark:text-gray-500">Rate: Not set</span>
          )}
          <span className="mx-2 text-gray-300 dark:text-gray-600">•</span>
          <span>This week: {formatHours(hoursThisWeek)}</span>
          <span className="mx-2 text-gray-300 dark:text-gray-600">•</span>
          <span>
            Earned this week:{' '}
            {hourlyRate?.amount
              ? formatCurrency(earningsThisWeek || computed.spentHours * (hourlyRate.amount || 0), hourlyRate.currency || 'USD')
              : '$0.00'}
          </span>
        </div>

        {alerts?.length > 0 && (
          <div className="space-y-2">
            {alerts.map((alert, index) => {
              if (isValidElement(alert)) {
                return (
                  <div key={index}>
                    {alert}
                  </div>
                );
              }

              if (typeof alert === 'string') {
                return (
                  <div
                    key={index}
                    className={cn(
                      'text-sm px-3 py-2 rounded-lg border',
                      alert.startsWith('❌')
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
                        : alert.startsWith('🚨')
                          ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800'
                          : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
                    )}
                  >
                    {alert}
                  </div>
                );
              }

              return (
                <div key={index} className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                  {alert}
                </div>
              );
            })}
          </div>
        )}

        {footer && (
          <div
            className="pt-4 border-t border-gray-100 dark:border-gray-700 transition-colors"
            onClick={(event) => event.stopPropagation()}
          >
            {footer}
          </div>
        )}
      </div>
    </article>
  );
};

export default ProjectCard;
export { getProgressColor };

